import { Button, Group, Loader } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useIntl } from 'react-intl';
import { fetchImagesFromFolderWithToken } from '@commons/storageApi';
import { useAppSelector } from '@store';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { ImageFile } from '../fields/types';

interface ReImportButtonProps {
  folderPath: string;
  onImagesImported: (images: ImageFile[]) => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  align?: 'left' | 'right';
}

/**
 * Shared component for re-importing images from DigiStorage
 * Used in both direct imagePicker fields and nested imagePicker inputs in infinite-list
 */
export const ReImportButton = ({
  folderPath,
  onImagesImported,
  size = 'xs',
  align = 'right',
}: ReImportButtonProps) => {
  const intl = useIntl();
  const { settings } = useAppSelector((state) => state.settings);
  const [isReImporting, setIsReImporting] = useState(false);

  const handleReImport = async () => {
    if (!folderPath) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message:
          intl.formatMessage({ id: 'folderPathRequired' }) ||
          'Folder path is required',
        color: 'red',
      });
      return;
    }

    if (!settings?.digiStorageToken) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message:
          intl.formatMessage({ id: 'digiStorageTokenRequired' }) ||
          'DigiStorage token is required',
        color: 'red',
      });
      return;
    }

    setIsReImporting(true);
    try {
      const images = await fetchImagesFromFolderWithToken(
        settings.digiStorageToken,
        folderPath,
        {
          failedToAuthenticate: intl.formatMessage({
            id: 'digiStorageFailedToAuthenticate',
          }),
          invalidResponseTokenNotFound: intl.formatMessage({
            id: 'digiStorageInvalidResponseTokenNotFound',
          }),
          networkError: intl.formatMessage({ id: 'digiStorageNetworkError' }),
          failedToGetMountId: intl.formatMessage({
            id: 'digiStorageFailedToGetMountId',
          }),
          noMountsFound: intl.formatMessage({ id: 'digiStorageNoMountsFound' }),
          failedToListFolder: intl.formatMessage({
            id: 'digiStorageFailedToListFolder',
          }),
          folderNotFound: intl.formatMessage({
            id: 'digiStorageFolderNotFound',
          }),
          failedToGetDownloadLink: intl.formatMessage({
            id: 'digiStorageFailedToGetDownloadLink',
          }),
          invalidResponseDownloadLinkNotFound: intl.formatMessage({
            id: 'digiStorageInvalidResponseDownloadLinkNotFound',
          }),
          failedToFetchImages: intl.formatMessage({
            id: 'digiStorageFailedToFetchImages',
          }),
        }
      );

      onImagesImported(images || []);

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'imagesReImported' }),
        color: 'green',
      });
    } catch (error: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: error.message || intl.formatMessage({ id: 'reImportFailed' }),
        color: 'red',
      });
    } finally {
      setIsReImporting(false);
    }
  };

  return (
    <Group justify={align === 'left' ? 'flex-start' : 'flex-end'}>
      <Button
        variant="default"
        size={size}
        leftSection={
          isReImporting ? (
            <Loader size={size === 'xs' ? 12 : 14} />
          ) : (
            <IconRefresh size={size === 'xs' ? 12 : 14} />
          )
        }
        onClick={handleReImport}
        disabled={isReImporting || !settings?.digiStorageToken}
      >
        {isReImporting
          ? intl.formatMessage({ id: 'reImporting' })
          : intl.formatMessage({ id: 'reImportNow' })}
      </Button>
    </Group>
  );
};
